import { useLocation, useNavigate } from 'react-router'; // useLocation
import { useState } from 'react';
import { useEffect, useRef } from 'react';
import OnboardingFooter from '../../components/OnboardingFooter';
import SetNewPassword from '../../views/SetNewPassword';
import InfoContent from '../../components/InfoContent';
import GreenCheckIcon from '../../assets/icons/check-green-icon.svg?react';
import { useApiClients } from '../../client/api/useApiClient';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import Button from '../../components/Button';
import BlueLinkIcon from '../../assets/icons/blue-link-icon.svg?react';

import { useButtonSizeLarge } from '../../utils/buttonSizes';
import {
  CheckTokenValidityDTOTypeEnum,
  CheckTokenValidityDTOResponse,
} from '@EHSNavigator/backend';
import Loader from '../../components/Loader';

// type ResendVerificationDTO = {
//   userId: string;
// };

export default function SetNewPasswordPage() {
  const navigate = useNavigate();
  const [successFullyReset, setSuccessFullyReset] = useState(false);
  const [tokenState, setTokenState] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // const [verificationMessage, setVerifiedMessage] = useState('');

  const location = useLocation();
  const query = new URLSearchParams(location?.search);
  const userId = query.get('userId');
  const token = query.get('token');
  const showMessage = query.get('showMessage');
  const verificationAttempted = useRef(false);
  const { AuthApi } = useApiClients();

  const tokenVerification = useMutation<
    CheckTokenValidityDTOResponse,
    Error,
    {
      checkTokenValidityDTO: {
        token: string;
        userId: string;
        type: CheckTokenValidityDTOTypeEnum;
      };
    }
  >({
    mutationFn: async (data) => {
      const response = await AuthApi.authControllerCheckTokenValidity(data);
      return response.data;
    },
    onSuccess: (data) => {
      let tokenState = '';
      const { valid, expired } = data;
      if (valid && !expired) {
        tokenState = 'Valid';
      } else if (!valid && expired) {
        tokenState = 'Expired';
      } else if (!valid && !expired) {
        tokenState = 'Invalid';
      }
      setTokenState(tokenState);
      setIsLoading(false);
      setIsValid(true);
    },
    onError: (error: any) => {
      setIsLoading(false);
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        // setVerifiedMessage(errorMessage);
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
      setIsValid(false);
    },
  });

  useEffect(() => {
    if (userId && token && !verificationAttempted.current) {
      verificationAttempted.current = true;
      tokenVerification.mutate({
        checkTokenValidityDTO: {
          token: String(token),
          userId: String(userId),
          type: CheckTokenValidityDTOTypeEnum.SetPassword,
        },
      });
    }
  }, [userId, token]);

  const buttonSize = useButtonSizeLarge();

  if (isLoading) {
    return <Loader />;
  }

  return (
    <main className="relative z-10 flex flex-1 flex-col items-center justify-between gap-10 pb-5">
      {!isValid && tokenState === '' && (
        <div className="mx-auto flex w-full max-w-685 flex-1 flex-col items-center gap-12 px-25 pt-14 md:pt-16 lg:pt-20">
          <BlueLinkIcon className="[&>path]:fill-brand-byzantine h-100 w-100 lg:h-150 lg:w-150" />
          <div className="flex flex-col gap-2.5 text-center">
            <h2 className={clsx('text-neutral-tones-text font-bold')}>
              Something went wrong
            </h2>
            <p className="font-inter-variable text-brand-byzantine text-2xl font-medium italic">
              There is Something went wrong!
            </p>
          </div>
          <Button
            onClick={() => navigate('/sign-in')}
            otherClasses={clsx('max-w-465 w-full')}
            size={buttonSize}
          >
            Go to Sign In
          </Button>
        </div>
      )}
      {isValid && tokenState === 'Valid' && (
        <>
          {!successFullyReset ? (
            <div className="flex w-full flex-1 flex-col items-center pt-14 md:pt-16 lg:pt-20">
              <div className="mb-7 flex flex-col items-center gap-2.5 px-10 text-center sm:px-0 md:mb-20 lg:mb-75">
                <img
                  src="/assets/images/logo.png"
                  alt="logo"
                  className="h-85 w-69 md:h-100 md:w-20 lg:h-150 lg:w-121"
                />
                <h2 className="text-brand-byzantine font-bold">
                  Set New Password
                </h2>
              </div>
              <div className="w-full px-25">
                <SetNewPassword
                  changeSuccessfuly={() => setSuccessFullyReset(true)}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-1 flex-col justify-center">
              <InfoContent
                icon={
                  <GreenCheckIcon className="h-100 w-100 lg:h-150 lg:w-150" />
                }
                title="Password has been set!"
                description="You can sign in with your new password."
                buttonLabel="Sign In"
                handleClick={() =>
                  navigate(
                    showMessage === 'true'
                      ? `/sign-in?message=Activation%20link%20sent—check%20your%20email%20to%20finish%20signing%20up.`
                      : '/sign-in',
                  )
                }
              />
            </div>
          )}
        </>
      )}
      {isValid && tokenState === 'Expired' && (
        <div className="mx-auto flex w-full max-w-685 flex-1 flex-col items-center gap-12 px-25 pt-14 md:pt-16 lg:pt-20">
          <BlueLinkIcon className="[&>path]:fill-brand-byzantine h-100 w-100 lg:h-150 lg:w-150" />
          <div className="flex flex-col gap-2.5 text-center">
            <h2 className={clsx('text-neutral-tones-text font-bold')}>
              Link Expired
            </h2>
            <p className="font-inter-variable text-brand-byzantine text-2xl font-medium italic">
              Token has been expired please click to the button below
            </p>
          </div>
          <Button
            onClick={() => navigate('/forgot-password')}
            otherClasses={clsx('max-w-465 w-full')}
            size={buttonSize}
          >
            Back to Forget Password
          </Button>
        </div>
      )}
      {isValid && tokenState === 'Invalid' && (
        <>
          <div className="mx-auto flex w-full max-w-685 flex-1 flex-col items-center gap-12 px-25 pt-14 md:pt-16 lg:pt-20">
            <BlueLinkIcon className="[&>path]:fill-brand-byzantine h-100 w-100 lg:h-150 lg:w-150" />
            <div className="flex flex-col gap-2.5 text-center">
              <h2 className={clsx('text-neutral-tones-text font-bold')}>
                Invalid Token
              </h2>
              <p className="font-inter-variable text-brand-byzantine text-2xl font-medium italic">
                The link is invalid or has already been used. Please sign in or
                request a new password reset.
              </p>
            </div>
            <Button
              onClick={() => navigate('/sign-in')}
              otherClasses={clsx('max-w-465 w-full')}
              size={buttonSize}
            >
              Go to Sign In
            </Button>
          </div>
        </>
      )}
      <OnboardingFooter />
    </main>
  );
}
